(() => {
  'use strict';
  const normalise = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const fabricKey = value => {
    const key = normalise(value);
    if (/^(plush|velvet|plush velvet|classic plush|italian plush)$/.test(key)) return 'plush velvet';
    if (/^(luxe chenille|signature luxe|luxury chenille|velour)$/.test(key)) return 'luxe chenille';
    if (/^(rove|chenille extra|chenille \+ extra)$/.test(key)) return 'chenille + extra';
    return key;
  };
  const colourKey = (fabric, colour) => {
    const key = normalise(colour);
    const aliases = {
      'plush velvet': {cream:'vanilla cream',mink:'champagne mink',blue:'pacific',steel:'smoked steel','steel grey':'smoked steel','plush grey':'smoked steel',black:'midnight noir','black noir':'midnight noir',noir:'midnight noir',rust:'burnt orange',olive:'olive green'},
      'luxe chenille': {coffee:'crimsy truffle',mocha:'latte','luxe mocha':'latte','luxe steel':'smoked steel','luxe steel grey':'smoked steel','steel grey':'smoked steel'},
      boucle: {cream:'meringue','boucle cream':'meringue',noir:'midnight','boucle noir':'midnight','storm grey':'steel','matcha green':'moss'}
    };
    return aliases[fabricKey(fabric)]?.[key] || key;
  };
  const optionIndex = (names, name) => names.findIndex(n => normalise(n) === name || name === 'colour' && normalise(n) === 'color');
  const isFinish = name => /^(fabric|colour|color|title)$/.test(normalise(name));
  const nativeOption = (names, variant, name) => variant.options[optionIndex(names, name)] || '';

  // Pure selection logic is shared by the component and its offline checks.
  function resolve(data, input, choices = {}) {
    const rule = data.rule;
    const sofa = data.sofaVariants.find(v => String(v.id) === String(input.id));
    if (!sofa || !rule.eligibleSofaIds.includes(String(sofa.id))) return {eligible:false};
    const quantity = Number(input.quantity || 1);
    const properties = {...input.properties};
    const fabric = properties.Fabric || input.finish?.fabricLabel || nativeOption(data.sofaOptions, sofa, 'fabric');
    const colour = properties.Colour || properties.Color || input.finish?.colour || nativeOption(data.sofaOptions, sofa, 'colour');
    if (fabric) properties.Fabric = fabric;
    if (colour) properties.Colour = colour;
    const base = {eligible:true,sofa,quantity,properties,fabric,colour};
    if (!sofa.available || !Number.isInteger(quantity) || quantity < 1 || input.kind && !['standard','custom'].includes(input.kind)) return {...base,ready:false};
    if (rule.mode === 'redirect') {
      const pair = rule.bundlePairs.find(p => p.sofaVariantId === String(sofa.id));
      const foot = data.footVariants.find(v => String(v.id) === pair?.bundleVariantId);
      if (!foot?.available || foot.price - sofa.price !== 29900) return {...base,ready:false};
      const params = new URLSearchParams({variant:String(foot.id),quantity:String(quantity)});
      if (fabric) params.set('fabric', fabric);
      if (colour) params.set('colour', colour);
      return {...base,ready:true,foot,price:29900,total:foot.price*quantity,
        redirect: data.root.replace(/\/?$/, '/') + 'products/' + rule.bundleHandle + '?' + params.toString()};
    }
    if (rule.mode === 'upgrade') {
      const pair = rule.upgradePairs.find(p => p.sofaVariantId === String(sofa.id));
      const foot = data.footVariants.find(v => String(v.id) === pair?.bundleVariantId);
      if (!pair || !foot?.available || sofa.price !== pair.sofaPrice || foot.price !== pair.bundlePrice) return {...base,ready:false};
      return {...base,ready:true,foot,footProperties:{...properties},code:pair.code,price:foot.price - sofa.price,total:foot.price*quantity,upgrade:true};
    }
    let pool = data.footVariants.filter(v => rule.footVariantIds.includes(String(v.id)));
    data.footOptions.forEach((name, i) => {
      if (isFinish(name)) return;
      const values = [...new Set(pool.map(v => v.options[i]))];
      const common = nativeOption(data.sofaOptions, sofa, normalise(name));
      const chosen = choices[name] || (values.includes(common) ? common : values[0]);
      pool = pool.filter(v => v.options[i] === chosen);
    });
    const matchesFabric = v => !nativeOption(data.footOptions, v, 'fabric') || fabricKey(nativeOption(data.footOptions, v, 'fabric')) === fabricKey(fabric);
    const matchesColour = v => !!colour && colourKey(fabric, nativeOption(data.footOptions, v, 'colour')) === colourKey(fabric, colour);
    const exact = pool.filter(v => matchesFabric(v) && matchesColour(v));
    // A sold-out exact stock variant must not be silently replaced with another stock colour.
    const foot = exact.length ? exact.find(v => v.available) : pool.find(v => v.available && matchesFabric(v)) || pool.find(v => v.available);
    if (!foot) return {...base,ready:false};
    const discount = rule.discounts.find(d => d.price === foot.price && d.variantIds.includes(String(foot.id)));
    const price = foot.price;
    const footProperties = {};
    if (fabric) footProperties.Fabric = fabric;
    if (colour) footProperties.Colour = colour;
    data.footOptions.forEach((name,i) => {
      if (!isFinish(name) && new Set(data.footVariants.filter(v => rule.footVariantIds.includes(String(v.id))).map(v => v.options[i])).size > 1) footProperties[name] = foot.options[i];
    });
    return {...base,ready:true,foot,footProperties,price,code:discount?.code || '',total:sofa.price*quantity + price,upgrade:false};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {resolve,normalise,fabricKey,colourKey};
  if (typeof customElements === 'undefined' || customElements.get('ck-sofa-pair')) return;

  customElements.define('ck-sofa-pair', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.data = JSON.parse(this.querySelector('[data-pair-config]').textContent);
      this.data.root = this.data.root.replace(/\/?$/, '/');
      this.scope = this.closest('.product-information, .product__info, .product-info, product-info, .product') || document.querySelector('main') || document;
      this.button = this.querySelector('[data-pair-add]');
      this.error = this.querySelector('[data-pair-error]');
      this.choices = {};
      this.makeOptions();
      this.listen = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.update(), 120); };
      this.scope.addEventListener('change', this.listen);
      this.scope.addEventListener('ck:finish-selected', this.listen);
      document.addEventListener('variant:update', this.listen);
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.listen();
      });
      this.observer.observe(this.scope, {subtree:true,childList:true,attributes:true,attributeFilter:['value','data-ck-selection-kind']});
      this.button.addEventListener('click', () => this.addSet());
      this.update();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.scope?.removeEventListener('change', this.listen);
      this.scope?.removeEventListener('ck:finish-selected', this.listen);
      document.removeEventListener('variant:update', this.listen);
      this.initialised = false;
    }
    makeOptions() {
      if (['upgrade','redirect'].includes(this.data.rule.mode)) return;
      const host = this.querySelector('[data-pair-options]');
      host.replaceChildren();
      const pool = this.data.footVariants.filter(v => this.data.rule.footVariantIds.includes(String(v.id)));
      this.data.footOptions.forEach((name,i) => {
        const values = [...new Set(pool.map(v => v.options[i]))];
        if (isFinish(name) || values.length < 2) return;
        const label = document.createElement('label');
        const text = document.createElement('span');
        text.textContent = `${this.data.text.optionPrefix} ${name.toLowerCase()}`;
        const select = document.createElement('select');
        select.dataset.footOption = name;
        values.forEach(value => { const option = document.createElement('option'); option.value = value; option.textContent = value; select.append(option); });
        select.addEventListener('change', () => { this.choices[name] = select.value; this.update(); });
        label.append(text,select);
        host.append(label);
      });
    }
    selection() {
      const forms = this.scope.querySelectorAll('product-form-component form, form[action*="/cart/add"]');
      const form = Array.from(forms).find(f => this.data.sofaVariants.some(v => String(v.id) === String(new FormData(f).get('id'))));
      if (!form) return {eligible:true,ready:false};
      const input = new FormData(form);
      const nativePicker = this.scope.querySelector('variant-picker');
      if (nativePicker?.__ckPendingVariantId && String(nativePicker.__ckPendingVariantId) !== String(input.get('id'))) return {eligible:true,ready:false};
      const picker = this.scope.querySelector('[data-ck-selection-kind]');
      const properties = {};
      for (const [name,value] of input.entries()) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && !match[1].startsWith('_ck_complete_')) properties[match[1]] = value;
      }
      const sofa = this.data.sofaVariants.find(v => String(v.id) === String(input.get('id')));
      if (sofa) this.querySelectorAll('[data-foot-option]').forEach(select => {
        const common = nativeOption(this.data.sofaOptions, sofa, normalise(select.dataset.footOption));
        if (select.dataset.lastCommon !== common && Array.from(select.options).some(o => o.value === common)) {
          select.value = common;
          this.choices[select.dataset.footOption] = common;
        }
        select.dataset.lastCommon = common;
        if (!this.choices[select.dataset.footOption]) this.choices[select.dataset.footOption] = select.value;
      });
      return resolve(this.data, {id:input.get('id'),quantity:input.get('quantity') || 1,properties,kind:picker?.dataset.ckSelectionKind,finish:picker?.__ckGallerySelection}, this.choices);
    }
    money(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currency:this.data.currency,maximumFractionDigits:2,minimumFractionDigits:0}).format(value / 100);
    }
    update() {
      if (this.busy || !this.isConnected) return;
      const selected = this.selection();
      this.hidden = !selected.eligible;
      this.button.disabled = !selected.ready;
      this.button.textContent = this.data.rule.mode === 'redirect' ? this.data.text.viewBundle : this.data.rule.mode === 'upgrade' ? this.data.text.upgrade : this.data.text.button;
      this.querySelector('[data-pair-finish]').textContent = selected.ready ? [selected.fabric,selected.colour].filter(Boolean).join(' / ') : this.data.text.choose;
      this.querySelector('[data-pair-price]').textContent = selected.ready ? this.data.text.pairPrice.replace('[price]', this.money(selected.price)) : '';
      this.querySelector('[data-pair-total]').textContent = selected.ready ? this.data.text.total.replace('[price]', this.money(selected.total)) : '';
    }
    async request(path, body) {
      const response = await fetch(this.data.root + path, body === undefined ? {headers:{Accept:'application/json'},credentials:'same-origin'} : {
        method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},credentials:'same-origin',body:JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.data.text.failed);
      return result;
    }
    async addSet() {
      if (this.busy) return;
      const selected = this.selection();
      if (!selected.ready) { this.update(); return; }
      if (selected.redirect) { window.location.assign(selected.redirect); return; }
      this.busy = true;
      this.button.disabled = true;
      this.button.textContent = this.data.text.adding;
      this.error.hidden = true;
      const token = 'set-' + Date.now() + '-' + Math.random().toString(36).slice(2,10);
      let changed = false;
      try {
        const before = await this.request('cart.js');
        if (before.items.some(x => this.data.rule.footVariantIds.includes(String(x.variant_id)))) throw new Error(this.data.text.existing);
        const sofaProperties = {...selected.properties,_ck_complete_set:token,_ck_complete_role:selected.upgrade ? 'bundle' : 'sofa'};
        const footProperties = {...selected.footProperties,_ck_complete_set:token,_ck_complete_role:'footstool'};
        const items = selected.upgrade ? [{id:selected.foot.id,quantity:selected.quantity,properties:sofaProperties}] : [
          {id:selected.sofa.id,quantity:selected.quantity,properties:sofaProperties},
          {id:selected.foot.id,quantity:1,properties:footProperties}
        ];
        changed = true;
        await this.request('cart/add.js',{items});
        // Cart savings are applied after arrival. Adding products never depends on another offer.
        const cart = await this.request('cart.js');
        const added = cart.items.filter(x => x.properties?._ck_complete_set === token);
        const foot = added.find(x => String(x.variant_id) === String(selected.foot.id));
        const sofa = selected.upgrade || added.find(x => String(x.variant_id) === String(selected.sofa.id) && x.quantity === selected.quantity);
        if (!foot || foot.quantity !== (selected.upgrade ? selected.quantity : 1) || !sofa) throw new Error(this.data.text.failed);
        window.location.assign(this.data.root + 'cart');
      } catch (error) {
        let message = error.message || this.data.text.failed;
        if (changed) {
          try {
            const cart = await this.request('cart.js');
            const updates = {};
            cart.items.filter(x => x.properties?._ck_complete_set === token).forEach(x => { updates[x.key] = 0; });
            if (Object.keys(updates).length) await this.request('cart/update.js',{updates});
          } catch (_) { message = this.data.text.review; }
        }
        this.error.textContent = message;
        this.error.hidden = false;
        this.busy = false;
        this.update();
      }
    }
  });
})();

